import { activities as activitiesData, projects } from "../../../db";
import {
  calculateDurationInMonths,
  calculateProjectActivityIntersection,
  normalizeSkillFormat,
  createSkillMaps,
  addSkillToMap,
  normalizeSkillMaps,
  type SkillMaps,
  type NormalizedSkillsByCategory
} from './skills/calculators/skillCalculationUtils';

export interface ActivitySkills {
  profession: Array<{ name: string; level: number; }>;
  lead: Array<{ name: string; level: number; }>;
  language: Array<{ name: string; level: number; }>;
  tools: Array<{ name: string; level: number; }>;
}

// Memoization cache for performance
const memoizedActivitySkillsCache = new Map<string, ActivitySkills>();

/**
 * Calculate skills gained from activity projects
 */
export function calculateActivitySkills(activityId: number): ActivitySkills {
  // Simple cache key based on activityId and projects data length
  const cacheKey = `${activityId}-${projects.length}`;
  
  if (memoizedActivitySkillsCache.has(cacheKey)) {
    return memoizedActivitySkillsCache.get(cacheKey)!;
  }
  const skillMaps = createSkillMaps();
  
  // Find the activity
  const activity = activitiesData.find(a => a.id === activityId);
  if (!activity) {
    return { profession: [], lead: [], language: [], tools: [] };
  }
  
  // Find all projects associated with this activity
  const activityProjects = projects.filter((p: any) => {
    // Skip private projects
    if (p.activityId === null) {
      return false;
    }
    
    if (Array.isArray(p.activityId)) {
      // If project has multiple activities, check if this activity is included
      return p.activityId.includes(activityId);
    }
    // If project has single activity, check if it matches
    return p.activityId === activityId;
  });
  
  // Calculate total duration of ALL activity projects for normalization
  // For multi-activity projects, use time intersection instead of simple proportional split
  const totalActivityProjectMonths = activityProjects.reduce((total, project) => {
    return total + calculateProjectActivityIntersection(project, activity);
  }, 0);
  
  activityProjects.forEach((project: any) => {
    // Calculate how much of this project's duration should be attributed to this activity
    // Use time intersection for continued projects instead of simple proportional split
    const duration = calculateProjectActivityIntersection(project, activity);
    
    // Get normalized skill format (handles both legacy and new formats)
    const normalizedSkills = normalizeSkillFormat(project);
    
    // Process all skill categories
    Object.entries(normalizedSkills).forEach(([category, skills]) => {
      const categoryKey = category as keyof SkillMaps;
      skills.forEach((skill: any) => {
        if (!skill.name || typeof skill.weight !== 'number') return;
        
        addSkillToMap(skillMaps, categoryKey, skill.name, duration, skill.weight);
      });
    });
  });
  
  // Calculate normalized skill levels (0-100) for each category
  // Important: Normalize against ALL activity project duration, not just projects where skill appears
  const skillsByCategory = normalizeSkillMaps(skillMaps, totalActivityProjectMonths);

  memoizedActivitySkillsCache.set(cacheKey, skillsByCategory);
  return skillsByCategory;
}

/**
 * Get total skills count for an activity
 */
export function getActivitySkillsCount(activityId: number): number {
  const skills = calculateActivitySkills(activityId);
  return Object.values(skills).reduce((total, categorySkills) => total + categorySkills.length, 0);
}

/**
 * Generate URL-friendly slug from activity title
 */
export function createActivitySlug(activityTitle: string): string {
  return activityTitle
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-|-$/g, '');
}

/**
 * Find activity by slug
 */
export function findActivityBySlug(slug: string): typeof activitiesData[0] | undefined {
  return activitiesData.find(activity => createActivitySlug(activity.title) === slug);
}
